import { useMemo, type ReactElement } from 'react';

export interface MetricSeries {
  key: string;
  /** Valores alineados 1:1 con `categories`. */
  values: number[];
}

interface MetricChartProps {
  categories: string[];
  series: MetricSeries[];
  /** Formatea el valor del eje Y (ej. nanoToUsdCompact). */
  formatValue?: (n: number) => string;
  maxCategories?: number;
}

const PALETTE: string[] = [
  'var(--accent)',
  'var(--k-llm)',
  'var(--k-tts)',
  'var(--k-stt)',
  'var(--k-tool_call)',
  'var(--k-module_step)',
  'var(--k-retrieval)',
  'var(--k-pipeline_stage)',
];

/**
 * Barras verticales apiladas en SVG plano (sin recharts, decisión del repo).
 * Cada categoría es una barra; cada serie un segmento apilado. Generaliza el
 * StackedAreaChart de Costs.tsx para reuso entre reportes.
 */
export function MetricChart({
  categories,
  series,
  formatValue,
  maxCategories = 16,
}: MetricChartProps): ReactElement {
  const { cats, stacks, maxTotal } = useMemo(() => {
    // Limitar a las primeras N categorías para legibilidad.
    const idxs = categories.map((_, i) => i).slice(0, maxCategories);
    const totals = idxs.map((i) => series.reduce((a, s) => a + (s.values[i] ?? 0), 0));
    const max = totals.reduce((m, t) => Math.max(m, t), 0);
    return {
      cats: idxs.map((i) => categories[i] ?? ''),
      stacks: idxs.map((i) => series.map((s) => ({ key: s.key, value: s.values[i] ?? 0 }))),
      maxTotal: max,
    };
  }, [categories, series, maxCategories]);

  if (cats.length === 0 || maxTotal === 0) {
    return <div className="empty">Sin datos para graficar.</div>;
  }

  const width = 800;
  const height = 240;
  const padL = 48;
  const padB = 36;
  const innerW = width - padL - 16;
  const innerH = height - padB - 12;
  const slot = innerW / cats.length;
  const barW = Math.min(slot * 0.6, 48);

  const yFor = (v: number): number => 12 + innerH - (v / maxTotal) * innerH;

  return (
    <div className="chart-host">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" data-testid="metric-chart">
        <text x={padL} y={12} fontSize={10} fill="var(--text-tertiary)">
          {formatValue ? formatValue(maxTotal) : String(maxTotal)}
        </text>
        {stacks.map((segs, ci) => {
          const x = padL + slot * ci + (slot - barW) / 2;
          let acc = 0;
          return (
            <g key={cats[ci]}>
              {segs.map((seg, si) => {
                const y0 = yFor(acc);
                acc += seg.value;
                const y1 = yFor(acc);
                const h = Math.max(0, y0 - y1);
                if (h <= 0) return null;
                return (
                  <rect
                    key={seg.key}
                    x={x}
                    y={y1}
                    width={barW}
                    height={h}
                    fill={PALETTE[si % PALETTE.length] ?? 'var(--accent)'}
                    opacity={0.82}
                  />
                );
              })}
              <text
                x={padL + slot * ci + slot / 2}
                y={height - 8}
                fontSize={9}
                fill="var(--text-tertiary)"
                textAnchor="middle"
              >
                {(cats[ci] ?? '').slice(0, 10)}
              </text>
            </g>
          );
        })}
      </svg>
      {series.length > 1 ? (
        <div className="chart-legend">
          {series.map((s, i) => (
            <span key={s.key}>
              <span
                className="chart-legend-swatch"
                style={{ background: PALETTE[i % PALETTE.length] ?? 'var(--accent)' }}
              />
              {s.key}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
